import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type DemoModeService from 'tio-employee/services/demo-mode';
import { getLoanSummaryRequest, wellnessInfoRequest } from 'tio-data/requests/user';
import { wellnessNsldsPrefill, withTestLoanCards } from 'tio-employee/utils/idr-dashboard-0726';

const EMPTY_LOAN_SUMMARY: LoanSummary = { summaryByLoanType: [], errors: [] };

interface QuestionnaireParams {
  direct?: string;
  ffel?: string;
  plus?: string;
}

export default class AuthenticatedIdrDashboard0726QuestionnaireRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare demoMode: DemoModeService;

  queryParams = {
    direct: { refreshModel: true },
    ffel: { refreshModel: true },
    plus: { refreshModel: true },
  };

  async model(params: QuestionnaireParams) {
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id as string;

    const parentWellness =
      (this.modelFor('authenticated') as { wellnessData?: unknown } | undefined)?.wellnessData ??
      null;

    const [loanSummaryResult, personResult, wellnessResult] = await Promise.allSettled([
      userId
        ? this.store.request(getLoanSummaryRequest(userId))
        : Promise.resolve({ content: EMPTY_LOAN_SUMMARY }),
      this.store.request(
        findRecord<PersonModel>('person', personId, {
          include: 'user.answer-profile',
          reload: true,
        })
      ) as Promise<{ content: { data: PersonModel } }>,
      this.demoMode.isDemoSessionUser || parentWellness != null || !userId
        ? Promise.resolve({ content: parentWellness })
        : this.store.request(wellnessInfoRequest(userId)),
    ]);

    const loanSummary =
      loanSummaryResult.status === 'fulfilled'
        ? ((loanSummaryResult.value.content as LoanSummary | undefined) ?? EMPTY_LOAN_SUMMARY)
        : EMPTY_LOAN_SUMMARY;
    const person = personResult.status === 'fulfilled' ? personResult.value.content.data : null;
    const nsldsPrefill = wellnessNsldsPrefill(
      wellnessResult.status === 'fulfilled'
        ? (wellnessResult.value.content as Parameters<typeof wellnessNsldsPrefill>[0])
        : null
    );

    return {
      loanSummary: withTestLoanCards(loanSummary, {
        direct: params.direct === 'true',
        ffel: params.ffel === 'true',
        plus: params.plus === 'true',
      }),
      user: person?.user ?? this.sessionContext.user,
      nsldsPrefill,
    };
  }
}
